import moment from 'moment-timezone'

/**
 * Centralized date utility functions using moment.js
 * Provides consistent, timezone-safe date operations across the application
 */

// ==================== DATE FORMATTING ====================

/**
 * Format a date for display with weekday and date
 * @param {Date|string|moment} date - The date to format
 * @returns {string} Formatted date (e.g., "Mon, Jan 15")
 */
export const formatWorklogDate = (date) => {
    return moment(date).format('ddd, MMM D')
}

/**
 * Format a date range for display
 * @param {Date|string|moment} from - Start date
 * @param {Date|string|moment} to - End date
 * @returns {string} Formatted date range
 */
export const formatDateRange = (from, to) => {
    const fromMoment = moment(from)
    const toMoment = moment(to)

    if (fromMoment.isSame(toMoment, 'day')) {
        return fromMoment.format('MMM D, YYYY')
    }

    if (fromMoment.isSame(toMoment, 'year')) {
        return `${fromMoment.format('MMM D')} - ${toMoment.format('MMM D, YYYY')}`
    }

    return `${fromMoment.format('MMM D, YYYY')} - ${toMoment.format('MMM D, YYYY')}`
}

/**
 * Format date for backend API (YYYY-MM-DD)
 * @param {Date|string|moment} date - The date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateForBackend = (date) => {
    return moment(date).format('YYYY-MM-DD')
}

/**
 * Format date for display in tables and lists
 * @param {Date|string|moment} date - The date to format
 * @returns {string} Formatted date (e.g., "Jan 15, 2025")
 */
export const formatDisplayDate = (date) => {
    return moment(date).format('MMM D, YYYY')
}

/**
 * Format date for detailed display with weekday
 * @param {Date|string|moment} date - The date to format
 * @returns {string} Formatted date (e.g., "Monday, January 15, 2025")
 */
export const formatLongDate = (date) => {
    return moment(date).format('dddd, MMMM D, YYYY')
}

/**
 * Format date for calendar headers
 * @param {Date|string|moment} date - The date to format
 * @returns {string} Formatted date (e.g., "January 2025")
 */
export const formatMonthYear = (date) => {
    return moment(date).format('MMMM YYYY')
}

// ==================== TIME FORMATTING ====================

/**
 * Format time for display (12-hour format)
 * @param {Date|string|moment} time - The time to format
 * @returns {string} Formatted time (e.g., "2:30 PM")
 */
export const formatTime12Hour = (time) => {
    return moment(time).format('h:mm A')
}

/**
 * Format hour and minute to 12-hour format
 * @param {number} hour - Hour (0-23)
 * @param {number} minute - Minute (0-59)
 * @returns {string} Formatted time (e.g., "2:30 PM")
 */
export const formatHourMinute12Hour = (hour, minute) => {
    return moment().hour(hour).minute(minute).format('h:mm A')
}

/**
 * Format time for display (24-hour format)
 * @param {Date|string|moment} time - The time to format
 * @returns {string} Formatted time (e.g., "14:30")
 */
export const formatTime24Hour = (time) => {
    return moment(time).format('HH:mm')
}

/**
 * Format time range for display
 * @param {Date|string|moment} startTime - Start time
 * @param {Date|string|moment} endTime - End time
 * @returns {string} Formatted time range (e.g., "2:30 PM - 5:00 PM")
 */
export const formatTimeRange = (startTime, endTime) => {
    const start = moment(startTime)
    const end = moment(endTime)

    return `${start.format('h:mm A')} - ${end.format('h:mm A')}`
}

// ==================== DURATION FORMATTING ====================

/**
 * Format duration in seconds to HH:MM:SS
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "2:30:45")
 */
export const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '00:00:00'

    const duration = moment.duration(seconds, 'seconds')
    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()
    const secs = duration.seconds()

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/**
 * Format duration in seconds to human readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Human readable duration (e.g., "2h 30m")
 */
export const formatDurationHuman = (seconds) => {
    if (!seconds || seconds === 0) return '0m'

    const duration = moment.duration(seconds, 'seconds')
    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()

    if (hours === 0) return `${minutes}m`
    if (minutes === 0) return `${hours}h`
    return `${hours}h ${minutes}m`
}

// ==================== DATE CALCULATIONS ====================

/**
 * Get the start of the week (Monday) for a given date
 * @param {Date|string|moment} date - The date
 * @returns {moment.Moment} Monday of the week
 */
export const getWeekStart = (date) => {
    return moment(date).startOf('isoWeek') // Monday
}

/**
 * Get the end of the week (Sunday) for a given date
 * @param {Date|string|moment} date - The date
 * @returns {moment.Moment} Sunday of the week
 */
export const getWeekEnd = (date) => {
    return moment(date).endOf('isoWeek') // Sunday
}

/**
 * Get an array of dates for a week starting from Monday
 * @param {Date|string|moment} date - Any date in the week
 * @param {boolean} includeWeekends - Whether to include Saturday and Sunday
 * @returns {moment.Moment[]} Array of dates in the week
 */
export const getWeekDates = (date, includeWeekends = true) => {
    const weekStart = getWeekStart(date)
    const daysToShow = includeWeekends ? 7 : 5
    const dates = []

    for (let i = 0; i < daysToShow; i++) {
        dates.push(moment(weekStart).add(i, 'days'))
    }

    return dates
}

/**
 * Format week range for display
 * @param {Date|string|moment} date - Any date in the week
 * @returns {string} Formatted week range (e.g., "Dec 8 - Dec 14")
 * Note: Always includes month for both dates to match backend format
 */
export const formatWeekRange = (date) => {
    const weekStart = getWeekStart(date)
    const weekEnd = getWeekEnd(date)

    // Always include month for both dates to match backend format
    // Backend always returns "MMM D - MMM D" (e.g., "Dec 8 - Dec 14")
    return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`
}

/**
 * Get date range for preset options (Today, Yesterday, This week, etc.)
 * @param {string} preset - The preset option
 * @returns {Object} Object with 'from' and 'to' date strings (YYYY-MM-DD)
 */
export const getDateRangeForPreset = (preset) => {
    const today = moment()

    switch(preset) {
        case 'Today':
            return {
                from: today.format('YYYY-MM-DD'),
                to: today.format('YYYY-MM-DD')
            }

        case 'Yesterday':
            const yesterday = moment().subtract(1, 'day')
            return {
                from: yesterday.format('YYYY-MM-DD'),
                to: yesterday.format('YYYY-MM-DD')
            }

        case 'This week':
            return {
                from: getWeekStart(today).format('YYYY-MM-DD'),
                to: getWeekEnd(today).format('YYYY-MM-DD')
            }

        case 'Last week':
            const lastWeekStart = getWeekStart(today).subtract(1, 'week')
            const lastWeekEnd = getWeekEnd(lastWeekStart)
            return {
                from: lastWeekStart.format('YYYY-MM-DD'),
                to: lastWeekEnd.format('YYYY-MM-DD')
            }

        case 'Past two weeks':
            const twoWeeksAgo = getWeekStart(today).subtract(2, 'weeks')
            return {
                from: twoWeeksAgo.format('YYYY-MM-DD'),
                to: getWeekEnd(today).format('YYYY-MM-DD')
            }

        case 'This month':
            return {
                from: moment().startOf('month').format('YYYY-MM-DD'),
                to: moment().endOf('month').format('YYYY-MM-DD')
            }

        case 'Last month':
            const lastMonth = moment().subtract(1, 'month')
            return {
                from: lastMonth.startOf('month').format('YYYY-MM-DD'),
                to: lastMonth.endOf('month').format('YYYY-MM-DD')
            }

        case 'This year':
            return {
                from: moment().startOf('year').format('YYYY-MM-DD'),
                to: moment().endOf('year').format('YYYY-MM-DD')
            }

        case 'Last year':
            const lastYear = moment().subtract(1, 'year')
            return {
                from: lastYear.startOf('year').format('YYYY-MM-DD'),
                to: lastYear.endOf('year').format('YYYY-MM-DD')
            }

        default:
            return { from: '', to: '' }
    }
}

// ==================== DATE VALIDATION & COMPARISON ====================

/**
 * Check if a worklog spans overnight (end time is on the next day)
 * @param {Object} worklog - Worklog object with startTime and endTime
 * @returns {boolean} True if worklog spans overnight
 */
export const isOvernightWorklog = (worklog) => {
    const startTime = moment(worklog.startTime)
    const endTime = moment(worklog.endTime)

    return !startTime.isSame(endTime, 'day')
}

/**
 * Check if a date is today
 * @param {Date|string|moment} date - The date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
    return moment(date).isSame(moment(), 'day')
}

/**
 * Check if a date is in the current week
 * @param {Date|string|moment} date - The date to check
 * @returns {boolean} True if date is in current week
 */
export const isThisWeek = (date) => {
    return moment(date).isSame(moment(), 'isoWeek')
}

/**
 * Check if a date is in the current month
 * @param {Date|string|moment} date - The date to check
 * @returns {boolean} True if date is in current month
 */
export const isThisMonth = (date) => {
    return moment(date).isSame(moment(), 'month')
}

/**
 * Check if two dates are the same day
 * @param {Date|string|moment} date1 - First date
 * @param {Date|string|moment} date2 - Second date
 * @returns {boolean} True if dates are the same day
 */
export const isSameDay = (date1, date2) => {
    return moment(date1).isSame(moment(date2), 'day')
}

// ==================== CONVERSION UTILITIES ====================

/**
 * Convert time string to Date object for a specific date
 * @param {string} timeString - Time in HH:MM format
 * @param {Date|string|moment} date - The date to apply the time to
 * @returns {Date} Date object with the specified time
 */
export const timeStringToDate = (timeString, date = moment()) => {
    const [hours, minutes] = timeString.split(':').map(Number)
    return moment(date).hour(hours).minute(minutes).second(0).millisecond(0).toDate()
}

/**
 * Get current time formatted for display
 * @returns {string} Current time in 12-hour format
 */
export const getCurrentTimeFormatted = () => {
    return moment().format('h:mm:ss A')
}

/**
 * Calculate duration between two times in seconds
 * @param {Date|string|moment} startTime - Start time
 * @param {Date|string|moment} endTime - End time
 * @returns {number} Duration in seconds
 */
export const calculateDurationSeconds = (startTime, endTime) => {
    return moment(endTime).diff(moment(startTime), 'seconds')
}

// ==================== EXPORT ALL ====================

// Export moment for direct use when needed
export { moment }

// ==================== CALENDAR LAYOUT UTILITIES ====================

/**
 * Get the slot height in pixels based on view density
 * This must match the --calendar-slot-height CSS variable
 * @param {string} viewDensity - 'relaxed' or 'compact'
 * @returns {number} Slot height in pixels
 */
export const getSlotHeight = (viewDensity = 'relaxed') => {
    return viewDensity === 'relaxed' ? 20 : 16
}

/**
 * Get the Monday of the week in YYYY-MM-DD format for URL query params
 * @param {Date|string|moment} date - Any date in the week
 * @returns {string} Monday date in YYYY-MM-DD format
 */
export const getMondayOfWeek = (date) => {
    return moment(date).startOf('isoWeek').format('YYYY-MM-DD')
}

// Default export with all utilities
export default {
    formatWorklogDate,
    formatDateRange,
    formatDateForBackend,
    formatDisplayDate,
    formatLongDate,
    formatMonthYear,
    formatTime12Hour,
    formatHourMinute12Hour,
    formatTime24Hour,
    formatTimeRange,
    formatDuration,
    formatDurationHuman,
    getWeekStart,
    getWeekEnd,
    getWeekDates,
    formatWeekRange,
    getDateRangeForPreset,
    getMondayOfWeek,
    isOvernightWorklog,
    isToday,
    isThisWeek,
    isThisMonth,
    isSameDay,
    timeStringToDate,
    getCurrentTimeFormatted,
    calculateDurationSeconds,
    getSlotHeight,
    moment,
    getMondayOfWeek
}